// The weather a visitor picks (the Weather chip, W): the city's own, drizzle, rain, thunderstorm, snow, autumn leaves or a clear night.
// Every scene draws its own weather; for a pick it doesn't have, it hides its own falling things and calls
// drawWeather at its three depths (far, mid, near) instead, telling it where each depth lands and which lights
// the drops catch and, where the view calls for it, what keeps the weather off (shelter) and that no tree is near
// enough to shed leaves. Loop-safe like every scene particle: n (integer) cycles per loop, placed by hash(id, cycle).
import { W, H, LOOP, TAU, lerp, cycle, wave, hash, rng, makeCanvas, makeHalo, px } from './core.js';

export const WEATHERS = ['city', 'drizzle', 'rain', 'thunderstorm', 'snow', 'leaves', 'clear'];
const SHARED = ['drizzle', 'rain', 'thunderstorm', 'snow', 'leaves'];

/**
 * What a scene draws for each pick. own: the kinds of falling things the scene draws itself, e.g. ['rain', 'leaves']
 * for London. 'drizzle', 'rain', 'snow' and 'leaves' stay the scene's own when picked; any other kind ('petals', 'blossoms', …)
 * shows only with the city's own weather. Returns { [weather]: plan }, plan = { [own kind]: true, shared }, where
 * shared is the kind drawWeather must draw instead, or null. An unknown weather gets the city's own (plan(w)).
 */
export function weatherPlans(own) {
  const plans = { city: { ...Object.fromEntries(own.map(k => [k, true])), shared: null }, clear: { shared: null } };
  for (const k of SHARED) plans[k] = own.includes(k) ? { [k]: true, shared: null } : { shared: k };
  return w => plans[w] || plans.city;
}

/* ================= Rain: streaks that catch the near lights, splashes where they land ================= */
// a proper shower, heavier than the drizzles some cities draw; the far drops are too far off to catch a lamp
const RAIN = [
  { count: 230, nMin: 150, nMax: 180, ff: 1, len: 2, slope: 0.12, col: '#9692ba', a: 0.34, lit: false, litA: 0, splash: 0 },
  { count: 150, nMin: 190, nMax: 220, ff: 0.9, len: 3, slope: 0.16, col: '#aca8d0', a: 0.42, lit: true, litA: 0.62, splash: 1.2 },
  { count: 70, nMin: 240, nMax: 270, ff: 0.82, len: 5, slope: 0.22, col: '#c8c8e8', a: 0.52, lit: true, litA: 0.78, splash: 2.5 },
];
// Fine, slower drops with no splashes; cities with their own drizzle keep it instead.
const DRIZZLE = RAIN.map((L, depth) => ({ ...L, count: Math.round(L.count * 0.4),
  nMin: Math.round(L.nMin * 0.55), nMax: Math.round(L.nMax * 0.55),
  len: depth + 1, a: L.a * 0.5, litA: L.litA * 0.55, splash: 0 }));
const STORM_RAIN = RAIN.map(L => ({ ...L, count: Math.round(L.count * 1.25),
  nMin: Math.round(L.nMin * 1.15), nMax: Math.round(L.nMax * 1.15), len: L.len + 1, slope: L.slope * 1.3 }));
// Sydney's distant-storm spacing, shared by the visuals and the existing thunder sound.
// One gentle two-second swell per event, at most six per four-minute loop; never a rapid sequence of flashes.
export const STORM_FLASHES = [
  { t: 32.4, x: 110 }, { t: 71.8, x: 370 }, { t: 104.6, x: 240 },
  { t: 139.2, x: 370 }, { t: 176.5, x: 110 }, { t: 211.9, x: 240 },
];
export const STORM_EVENTS = STORM_FLASHES.map(f => ({ t: +(f.t + 1).toFixed(1), type: 'thunder', x: f.x, gain: 0.3 }));
export function stormLight(t, reducedMotion = false) {
  if (reducedMotion) return 0;
  t = ((t % LOOP) + LOOP) % LOOP;
  const f = STORM_FLASHES.find(f => t >= f.t && t < f.t + 2);
  return f ? 0.06 * Math.sin(Math.PI * (t - f.t) / 2) ** 2 : 0;
}
let stormHalo;
function drawStormLight(ctx, t, land, reducedMotion) {
  const alpha = stormLight(t, reducedMotion);
  if (!alpha) return;
  const f = STORM_FLASHES.find(f => t >= f.t && t < f.t + 2);
  stormHalo ||= makeHalo(70, '#a6add2', 1, 1.4);
  ctx.globalAlpha = alpha;
  // The far weather pass stays behind foreground buildings, windows and shelter. No full-frame white flash.
  ctx.drawImage(stormHalo.c, f.x - 70, 0, 140, Math.min(90, land[0][0]));
  ctx.globalAlpha = 1;
}
/* ================= Snow: flakes that sway and drift, lit where they pass a lamp ================= */
const SNOW = [
  { count: 220, nMin: 18, nMax: 26, size: 1, col: '#a8bade', a: 0.62, amp: 2.5, drift: -10, lit: false },
  { count: 150, nMin: 28, nMax: 36, size: 1, col: '#dee8fa', a: 0.92, amp: 4.5, drift: -20, lit: true },
  { count: 60, nMin: 40, nMax: 50, size: 2, col: '#f2f6ff', a: 0.95, amp: 8, drift: -34, lit: true },
];
/* ================= Autumn leaves: plane-tree leaves that flutter and flip (as in London) ================= */
const LEAF_COLS = [
  { base: '#d8682a', dark: '#8e3a18', light: '#f6a650', back: '#b0522a', vein: '#e88a4a' },
  { base: '#9a5a2a', dark: '#5a3216', light: '#c8864a', back: '#7a4624', vein: '#aa6a3a' },
  { base: '#e0b040', dark: '#9a6a1e', light: '#fadc7c', back: '#bc8e36', vein: '#eec060' },
];
const LEAF_SHAPES = [
  ['11', '10'],
  ['10101', '11111', '01110', '00100'],
  ['000010000', '010010010', '011010110', '111111111', '011111110', '001111100', '000111000', '000010000'],
];
//             count nMin nMax amp drift
const LEAF_DEPTHS = [[30, 7, 10, 4, 24], [18, 10, 13, 7, 40], [9, 13, 16, 12, 60]];

// MAX_DENSITY times each depth's count, so a scene can ask for more (cfg.density); the first `count` are the default
const MAX_DENSITY = 2;
const particles = (defs, seed, extra) => defs.map((L, li) => {
  const r = rng(seed + li), out = [];
  for (let i = 0; i < L.count * MAX_DENSITY; i++) out.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), ...extra(r) });
  return out;
});
const DROPS = particles(RAIN, 7100, () => ({}));
const MIST_DROPS = particles(DRIZZLE, 7100, () => ({}));
const STORM_DROPS = particles(STORM_RAIN, 7100, () => ({}));
const FLAKES = particles(SNOW, 7200, r => ({ k: 20 + Math.floor(r() * 50), p: r() }));
const LEAVES = particles(LEAF_DEPTHS.map(([count, nMin, nMax]) => ({ count, nMin, nMax })), 7300, r => ({
  ks: 36 + Math.floor(r() * 30), p2: r(), kr: 20 + Math.floor(r() * 25), p3: r(), kf: 26 + Math.floor(r() * 40), p4: r(),
  amp: 0.7 + r() * 0.6, drift: 0.5 + r(), col: Math.floor(r() * 3),
}));

let LEAF_SPR = null;                                  // [colour][size][front, back], drawn on first use (needs a canvas)
/** The leaves and the storm's light are drawn again on their next use: the browser lost its graphics context, and its
 *  canvases with it (site/js/app.js contextRestored). */
export function forgetSprites() { LEAF_SPR = stormHalo = null; }
function makeLeaf(lc, size, back) {
  const S = LEAF_SHAPES[size], w = S[0].length, h = S.length, mid = w >> 1;
  const [c, x] = makeCanvas(w, h);
  S.forEach((row, j) => { for (let i = 0; i < w; i++) if (row[i] === '1') {
    let col = back ? lc.back : lc.base;
    if (size > 0 && i === mid && j > 0) col = back ? lc.vein : lc.dark;
    else if (!back && size > 0 && i < mid && j < h - 3 && (i + j) % 3 === 0) col = lc.light;
    else if (size === 2 && (j === h - 3 || i === 0 || i === w - 1)) col = lc.dark;
    px(x, i, j, col);
  } });
  return c;
}

/** the colour of the first light the point is in, or null. lights: [{ x, y, rx, ry, col? }] (col defaults to a warm lamp) */
const WARM = '#ffd08c';
function litAt(x, y, lights) {
  for (const l of lights) {
    const dx = (x - l.x) / l.rx, dy = (y - l.y) / l.ry;
    if (dx * dx + dy * dy < 1) return l.col || WARM;
  }
  return null;
}
const landY = (band, h) => lerp(band[0], band[1], h);
const upTo = (list, count, density) => Math.min(list.length, Math.round(count * density));
// flakes and leaves that land inside the frame fade over the last part of their fall instead of vanishing
const LAND_FADE = 0.12;
const landFade = (band, u) => (band[0] < H && u > 1 - LAND_FADE ? (1 - u) / LAND_FADE : 1);
// Shelter: how much of a drop, flake or leaf at (x, y) shows. Nothing inside a box, all of it from EDGE px outside,
// and a ramp between, so the weather thins out towards a roof or a doorway instead of ending on a ruled line.
const EDGE = 4;
const dryAt = (shelter, depth) => (shelter && shelter[depth] && shelter[depth].length ? shelter[depth] : null);
function shown(x, y, boxes) {
  let m = 1;
  for (const [x0, y0, x1, y1] of boxes) {
    const d = Math.max(x0 - x, x - x1, y0 - y, y - y1);   // how far outside the box (0 or less: inside)
    if (d <= 0) return 0;
    if (d < EDGE) m = Math.min(m, d / EDGE);
  }
  return m;
}

function drawRain(ctx, depth, t, land, lights, density, dry, defs = RAIN, particles = DROPS) {
  const L = defs[depth], band = land[depth], splashes = L.splash > 0 && band[0] < H, drops = particles[depth];
  for (let i = 0, n = upTo(drops, L.count, density); i < n; i++) {
    const d = drops[i];
    const { cyc, u } = cycle(t, d.n, d.ph);
    const yl = Math.round(landY(band, hash(d.id, cyc, 12))), fall = yl + 8 + L.len;   // every drop starts above the frame
    const xl = -24 + hash(d.id, cyc, 11) * (W + 24 + fall * L.slope);
    if (u < L.ff || !splashes) {
      const s = Math.min(1, u / L.ff), yh = yl - fall * (1 - s), xh = xl - (yl - yh) * L.slope;
      if (yh < -1 || yh > H) continue;
      let m = 1;
      if (dry && !(m = shown(xh, yh, dry))) continue;
      const lit = L.lit ? litAt(xh, yh, lights) : null;
      ctx.fillStyle = lit || L.col; ctx.globalAlpha = (lit ? L.litA : L.a) * m;
      for (let k = 0; k < L.len; k++) {
        const X = Math.round(xh - k * L.slope), Y = Math.round(yh - k);
        if (!dry || shown(X, Y, dry)) ctx.fillRect(X, Y, 1, 1);   // a streak that ends just under a box loses its tail there
      }
    } else {
      if (dry && shown(xl, yl, dry) < 1) continue;       // no splash where its droplets could land inside a box
      const s = (u - L.ff) / (1 - L.ff), hgt = Math.sin(Math.PI * s) * L.splash, spread = 1 + s * L.splash;
      const lit = L.lit ? litAt(xl, yl, lights) : null;
      ctx.fillStyle = lit || L.col; ctx.globalAlpha = (1 - s) * (lit ? L.litA : L.a) * 1.4;
      const Y = Math.round(yl - 1 - hgt);
      ctx.fillRect(Math.round(xl - spread), Y, 1, 1); ctx.fillRect(Math.round(xl + spread), Y, 1, 1);
      if (s < 0.35) ctx.fillRect(Math.round(xl), yl - 1, 1, 1);
    }
  }
  ctx.globalAlpha = 1;
}

function drawSnow(ctx, depth, t, land, lights, density, dry) {
  const L = SNOW[depth], band = land[depth], span = W + 40, flakes = FLAKES[depth];
  for (let i = 0, n = upTo(flakes, L.count, density); i < n; i++) {
    const f = flakes[i];
    const { cyc, u } = cycle(t, f.n, f.ph);
    const yEnd = landY(band, hash(f.id, cyc, 2)), y = -4 + u * (yEnd + 4);
    let x = hash(f.id, cyc, 1) * span + u * L.drift + L.amp * Math.sin(TAU * (f.k * t / LOOP + f.p));
    x = ((x % span) + span) % span - 20;
    const X = Math.round(x), Y = Math.round(y);
    let m = 1;
    if (dry && !(m = Math.min(shown(X, Y, dry), shown(X + L.size - 1, Y + L.size - 1, dry)))) continue;   // both corners of the flake
    const lit = L.lit ? litAt(x, y, lights) : null;
    ctx.fillStyle = lit || L.col; ctx.globalAlpha = (lit ? 0.98 : L.a) * landFade(band, u) * m;
    ctx.fillRect(X, Y, L.size, L.size);
  }
  ctx.globalAlpha = 1;
}

// Leaves with no tree to fall from (a high window, a rooftop, a desert): the wind carries them across the box
// `wind` = [x0, y0, x1, y1] instead, in at the left edge and out at the right, each at a height of its own that it
// gains or loses on the way. None passes close by: the near depth draws nothing and its leaves join the middle one.
// As many per column as fall, so a box narrower than the frame (a window) gets its share of them.
const WIND_PAD = 12, WIND_FADE = 0.08;
function drawLeaves(ctx, depth, t, land, density, dry, wind) {
  if (wind && depth === 2) return;
  if (!LEAF_SPR) LEAF_SPR = LEAF_COLS.map(lc => [0, 1, 2].map(s => [makeLeaf(lc, s, false), makeLeaf(lc, s, true)]));
  const [count, , , amp, drift] = LEAF_DEPTHS[depth], band = land[depth], S = LEAF_SHAPES[depth], w = S[0].length, h = S.length;
  const leaves = LEAVES[depth];
  const many = wind ? upTo(leaves, depth === 1 ? count + LEAF_DEPTHS[2][0] : count, density * Math.min(1, (wind[2] - wind[0]) / W))
    : upTo(leaves, count, density);
  for (let i = 0; i < many; i++) {
    const l = leaves[i];
    const { cyc, u } = cycle(t, l.n, l.ph), sw = wave(t, l.ks, l.p2);
    let x, y, a;
    if (wind) {
      x = lerp(wind[0] - WIND_PAD, wind[2] + WIND_PAD, u) + sw * amp * l.amp;
      y = lerp(wind[1], wind[3], lerp(hash(l.id, cyc, 6), hash(l.id, cyc, 5), u)) - Math.abs(sw) * (depth + 1) * 1.5;
      a = Math.min(1, u / WIND_FADE, (1 - u) / WIND_FADE);
    } else {
      const yEnd = landY(band, hash(l.id, cyc, 6)) + (band[0] >= H ? 12 : 0);
      y = -12 + u * (yEnd + 12) - Math.abs(sw) * (depth + 1) * 1.5;
      x = -30 + hash(l.id, cyc, 5) * (W + 20) + u * drift * l.drift + sw * amp * l.amp;
      a = landFade(band, u);
    }
    if (x < -12 || x > W + 12) continue;
    if (dry && !(a *= shown(x, y, dry))) continue;
    const fl = Math.cos(TAU * (l.kf * t / LOOP + l.p4)), spr = LEAF_SPR[(l.col + cyc) % 3][depth][fl < 0 ? 1 : 0];
    ctx.globalAlpha = a;
    if (depth === 0) { ctx.drawImage(spr, Math.round(x), Math.round(y)); continue; }
    ctx.save();
    ctx.translate(Math.round(x), Math.round(y));
    ctx.rotate(sw * 0.9 + 0.3 * wave(t, l.kr, l.p3));
    ctx.scale(1, Math.max(0.25, Math.abs(fl)));
    ctx.drawImage(spr, -(w >> 1), -(h >> 1));
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

// A light grade before the near depth, so a scene drawn for another season leans toward the pick: cold for snow,
// warm for autumn. Rain keeps the scene's own colours.
const GRADES = { snow: ['#9fb8ff', 0.14], leaves: ['#ff9a3c', 0.16] };
function tint(ctx, [col, a], w, h) {                  // the grade itself: a soft-light fill of the colour at that alpha
  ctx.globalCompositeOperation = 'soft-light'; ctx.globalAlpha = a; ctx.fillStyle = col;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 1;
}
// Over an opaque frame that fill sends each channel through its own fixed curve, so the frame is graded with a table
// per channel instead: built once per kind by tinting a strip of every level with the very same fill (so the tables
// paint exactly what the fill paints), then one read and one write of the frame. The fill is the priciest thing a
// pick adds (~0.4 ms a frame on a CPU canvas, ~1.5 ms at 4x CPU throttle); the tables save ~0.1-0.15 ms of it
// (~0.4-0.7 ms at 4x). They read pixels as little-endian words (R in the low byte), so a big-endian machine keeps
// the fill.
const LUTS = {}, LITTLE = new Uint8Array(Uint32Array.of(1).buffer)[0] === 1;
function gradeLut(g) {
  const [, x] = makeCanvas(256, 1), ramp = x.createImageData(256, 1);
  for (let i = 0; i < 256; i++) ramp.data.set([i, i, i, 255], i * 4);
  x.putImageData(ramp, 0, 0);
  tint(x, g, 256, 1);
  const lv = x.getImageData(0, 0, 256, 1).data;
  return [0, 1, 2].map(ch => Uint32Array.from({ length: 256 }, (_, i) => lv[i * 4 + ch] << (ch * 8)));   // r, g, b
}
function grade(ctx, kind) {
  const g = GRADES[kind];
  if (!g) return;
  if (!LITTLE) { tint(ctx, g, W, H); return; }
  if (!LUTS[kind]) LUTS[kind] = gradeLut(g);
  const [r, gr, b] = LUTS[kind];
  const img = ctx.getImageData(0, 0, W, H), u = new Uint32Array(img.data.buffer);
  for (let i = 0; i < u.length; i++) { const v = u[i]; u[i] = (v & 0xff000000) | b[v >>> 16 & 255] | gr[v >>> 8 & 255] | r[v & 255]; }
  ctx.putImageData(img, 0, 0);
}

const LAND = [[150, 200], [205, 245], [H + 4, H + 4]];
/**
 * Draws the shared weather `kind` ('drizzle' | 'rain' | 'thunderstorm' | 'snow' | 'leaves'; null or anything else draws nothing) at `depth`
 * (0 far, 1 mid, 2 near; the near call also grades the whole frame first, which is opaque by then and has no clip or
 * transform on it). cfg = { land, lights, density, shelter, leaves }; the final reducedMotion argument disables storm light:
 *   land    [[y0, y1] × 3]: the band each depth lands in (y0 >= H: that depth falls out of the bottom of the frame)
 *   lights  [{ x, y, rx, ry, col? }]: ellipses where drops and flakes catch the light (col: #rrggbb, default warm)
 *   density 1 by default, up to MAX_DENSITY: more of everything, for a scene that only shows a little of the sky
 *   shelter [[[x0, y0, x1, y1], …] × 3]: per depth, the boxes (edges included) nothing of that depth shows in: a room
 *           seen through a door, the roof the viewer stands under. Left out: none
 *   leaves  { wind: [x0, y0, x1, y1] }: no tree to fall from, so the wind carries leaves across this box at the far
 *           and the middle depth and the near depth has none (see drawLeaves). Left out: they fall from above
 */
export function drawWeather(ctx, kind, depth, t, { land = LAND, lights = [], density = 1, shelter, leaves } = {}, reducedMotion = false) {
  t = ((t % LOOP) + LOOP) % LOOP;
  const dry = dryAt(shelter, depth);
  if (depth === 2) grade(ctx, kind);
  if (kind === 'drizzle') drawRain(ctx, depth, t, land, lights, density, dry, DRIZZLE, MIST_DROPS);
  else if (kind === 'thunderstorm') {
    if (depth === 0) drawStormLight(ctx, t, land, reducedMotion);
    drawRain(ctx, depth, t, land, lights, density, dry, STORM_RAIN, STORM_DROPS);
  }
  else if (kind === 'rain') drawRain(ctx, depth, t, land, lights, density, dry);
  else if (kind === 'snow') drawSnow(ctx, depth, t, land, lights, density, dry);
  else if (kind === 'leaves') drawLeaves(ctx, depth, t, land, density, dry, leaves && leaves.wind);
}
